import type { Router } from "@remix-run/router";
import { createBrowserRouter, Navigate } from "react-router-dom";

import {
  guestAccessPath,
  guestSignedOutPath,
  SIGN_IN_PATH,
} from "@thalamos/haven-common";

import { RootComponent } from "../components/RootComponent/RootComponent.js";
import { AdminConfigurationScopePage } from "../pages/Admin/AdminConfigurationScopePage.js";
import { AdminConfigurationScopesPage } from "../pages/Admin/AdminConfigurationScopesPage.js";
import { AdminOrganisationConfigurationPage } from "../pages/Admin/AdminOrganisationConfigurationPage.js";
import { AdminOrganisationPage } from "../pages/Admin/AdminOrganisationPage.js";
import { AdminOrganisationsPage } from "../pages/Admin/AdminOrganisationsPage.js";
import { AdminPage } from "../pages/Admin/AdminPage.js";
import { AdminPersonPage } from "../pages/Admin/AdminPersonPage.js";
import { AdminUserPage } from "../pages/Admin/AdminUserPage.js";
import { AdminUsersPage } from "../pages/Admin/AdminUsersPage.js";
import { AuditTrailPage } from "../pages/Admin/AuditTrailPage.js";
import { ApprovalsPage } from "../pages/Approvals/ApprovalsPage.js";
import { AssessmentFullPage } from "../pages/Assessment/AssessmentFullPage.js";
import { AssessmentHome } from "../pages/Assessment/AssessmentHome.js";
import {
  AssessmentDomainLanding,
  AssessmentItemPage,
} from "../pages/Assessment/AssessmentItemPage.js";
import { AssessmentLayout } from "../pages/Assessment/AssessmentLayout.js";
import { AssessmentSummaryPage } from "../pages/Assessment/AssessmentSummaryPage.js";
import { ErrorPage } from "../pages/Error/ErrorPage.js";
import { FullHistoryPage } from "../pages/FullHistory/FullHistoryPage.js";
import {
  GuestAssessmentLayout,
  GuestSignedOutPage,
} from "../pages/Guest/GuestAssessmentLayout.js";
import { GuestAssessmentOverview } from "../pages/Guest/GuestAssessmentOverview.js";
import { GuestSentPage } from "../pages/Guest/GuestSentPage.js";
import { GuestSharedAssessmentPage } from "../pages/Guest/GuestSharedAssessmentPage.js";
import { GuestSharedPage } from "../pages/Guest/GuestSharedPage.js";
import { GuestSignInPage } from "../pages/Guest/GuestSignInPage.js";
import { HomeRoute } from "../pages/Home/HomeRoute.js";
import { MatchPersonPage } from "../pages/Persons/MatchPersonPage.js";
import { NewPersonPage } from "../pages/Persons/NewPersonPage.js";
import { PersonListingPage } from "../pages/Persons/PersonListingPage.js";
import { PersonPage } from "../pages/Persons/PersonPage.js";
import { ProfilePage } from "../pages/Profile/ProfilePage.js";
import { SessionContextSelectorPage } from "../pages/SessionContextSelector/SessionContextSelectorPage.js";
import { SignInPage } from "../pages/SignIn/SignInPage.js";
import { OrganisationUserPage } from "../pages/Users/OrganisationUserPage.js";
import { OrganisationUsersPage } from "../pages/Users/OrganisationUsersPage.js";
import { OrganisationUsersShell } from "../pages/Users/OrganisationUsersShell.js";

/**
 * Create a React Router representing the application's routes.
 *
 * The sign-in route sits outside `RootComponent` deliberately: everything inside
 * it requires a session, and rendering the sign-in page under a provider that
 * is fetching the user would fetch on every visit to it.
 *
 * The context selector sits *inside* it, because choosing a context needs the
 * user's memberships — but it is a sibling of the layout rather than a child, so
 * the shell does not render a team name while one is still being chosen.
 */
export const createRouter: () => Router = () => {
  return createBrowserRouter([
    {
      id: "sign-in",
      path: SIGN_IN_PATH,
      element: <SignInPage />,
      errorElement: <ErrorPage />,
    },
    // A guest's pages sit outside `RootComponent` as sign-in does: a guest
    // session has no staff user to fetch, and nothing of the staff app shows.
    {
      id: "guest-sign-in",
      path: guestAccessPath,
      element: <GuestSignInPage />,
      errorElement: <ErrorPage />,
    },
    {
      id: "guest-signed-out",
      path: guestSignedOutPath,
      element: <GuestSignedOutPage />,
      errorElement: <ErrorPage />,
    },
    {
      id: "guest-assessment",
      path: `${guestAccessPath}/assessment`,
      element: <GuestAssessmentLayout />,
      errorElement: <ErrorPage />,
      children: [
        {
          id: "guest-assessment-home",
          index: true,
          element: <GuestAssessmentOverview />,
        },
        {
          id: "guest-assessment-domain",
          path: "domains/:group",
          element: <AssessmentDomainLanding />,
        },
        {
          id: "guest-assessment-item",
          path: "domains/:group/:item",
          element: <AssessmentItemPage />,
        },
      ],
    },
    {
      id: "guest-sent",
      path: `${guestAccessPath}/sent`,
      element: <GuestSentPage />,
      errorElement: <ErrorPage />,
    },
    {
      id: "guest-shared",
      path: `${guestAccessPath}/shared`,
      element: <GuestSharedPage />,
      errorElement: <ErrorPage />,
    },
    {
      id: "guest-shared-assessment",
      path: `${guestAccessPath}/shared/:assessmentId`,
      element: <GuestSharedAssessmentPage />,
      errorElement: <ErrorPage />,
    },
    {
      id: "root",
      path: "/",
      element: <RootComponent />,
      errorElement: <ErrorPage />,
      children: [
        {
          errorElement: <ErrorPage />,
          children: [
            {
              // `HomeRoute` rather than the assessments page directly: where
              // `/` goes depends on the session context the session is acting
              // in, and Thalamos staff land in the administration area.
              id: "home",
              index: true,
              element: <HomeRoute />,
            },
            {
              id: "session-context-selector",
              path: "session-context-selector",
              element: <SessionContextSelectorPage />,
            },
            {
              // A sibling of the administration area rather than a page inside
              // it: everyone has an account to maintain, including staff acting
              // in the administrative session context, so it is not
              // administration.
              id: "profile",
              path: "profile",
              element: <ProfilePage />,
            },
            {
              id: "persons",
              path: "persons",
              element: <PersonListingPage />,
            },
            {
              id: "match-person",
              path: "persons/match",
              element: <MatchPersonPage />,
            },
            {
              id: "new-person",
              path: "persons/new",
              element: <NewPersonPage />,
            },
            {
              id: "person",
              path: "persons/:personId",
              element: <PersonPage />,
            },
            {
              // Not a tab of the profile: it is a view of the assessments, not
              // of the record. The static segment outranks the tab's
              // parameter.
              id: "full-history",
              path: "persons/:personId/full-history",
              element: <FullHistoryPage />,
            },
            {
              // The same page rather than a child of it: every tab shares the
              // record and its involvements, which the banner above the tabs
              // needs as well.
              id: "person-tab",
              path: "persons/:personId/:tab",
              element: <PersonPage />,
            },
            {
              // Nested so every screen of an assessment sits in one layout,
              // which holds the assessment open for them to share.
              id: "assessment",
              path: "assessments/:assessmentId",
              element: <AssessmentLayout />,
              children: [
                {
                  id: "assessment-home",
                  index: true,
                  element: <AssessmentHome />,
                },
                {
                  // A domain has no page of its own: opening one lands on the
                  // item it is to be carried on from.
                  id: "assessment-domain",
                  path: "domains/:group",
                  element: <AssessmentDomainLanding />,
                },
                {
                  id: "assessment-item",
                  path: "domains/:group/:item",
                  element: <AssessmentItemPage />,
                },
                {
                  id: "assessment-summary",
                  path: "summary",
                  element: <AssessmentSummaryPage />,
                },
                {
                  // A path rather than a query: what it is compared with is
                  // part of which summary this is, as the assessment is, where
                  // the grouping only lays it out.
                  id: "assessment-summary-comparison",
                  path: "summary/compared-with/:comparedId",
                  element: <AssessmentSummaryPage />,
                },
                {
                  id: "assessment-full",
                  path: "full",
                  element: <AssessmentFullPage />,
                },
              ],
            },
            {
              id: "approvals",
              path: "approvals",
              element: <ApprovalsPage />,
            },
            {
              // An organisation's admin's own accounts, outside `admin` because
              // that shell refuses every session but Thalamos's. Nested so this
              // shell's refusal covers every page beneath it.
              id: "users",
              path: "users",
              element: <OrganisationUsersShell />,
              children: [
                {
                  id: "users-index",
                  index: true,
                  element: <OrganisationUsersPage />,
                },
                {
                  id: "user",
                  path: ":userId",
                  element: <OrganisationUserPage />,
                },
              ],
            },
            {
              // Nested rather than one page switching on state, so each
              // administrative page has a URL that can be linked and reloaded.
              // `AdminPage` is the shell: it holds the search field and refuses
              // a session not acting in the administrative session context.
              id: "admin",
              path: "admin",
              element: <AdminPage />,
              children: [
                {
                  id: "admin-index",
                  index: true,
                  element: <Navigate to="/admin/users" replace />,
                },
                {
                  id: "admin-users",
                  path: "users",
                  element: <AdminUsersPage />,
                },
                {
                  // Under the `admin` shell like the listing rather than beside
                  // it, so the shell's session context check and its search
                  // field cover this page too — `adminPageForPath` resolves the
                  // path prefix to the Users page.
                  id: "admin-user",
                  path: "users/:userId",
                  element: <AdminUserPage />,
                },
                {
                  id: "admin-organisations",
                  path: "organisations",
                  element: <AdminOrganisationsPage />,
                },
                {
                  // Two routes onto the one page, so each tab has an address and
                  // the bare one opens on the first.
                  id: "admin-organisation",
                  path: "organisations/:organisationId",
                  element: <AdminOrganisationPage />,
                },
                {
                  id: "admin-organisation-tab",
                  path: "organisations/:organisationId/:tab",
                  element: <AdminOrganisationPage />,
                },
                {
                  id: "admin-configuration-chain",
                  path: "configuration/chain",
                  element: <AdminConfigurationScopesPage />,
                },
                {
                  id: "admin-configuration-scope",
                  path: "configuration/chain/:scopeId",
                  element: <AdminConfigurationScopePage />,
                },
                {
                  id: "admin-organisation-configuration",
                  path: "configuration/organisations",
                  element: <AdminOrganisationConfigurationPage />,
                },
                {
                  id: "admin-audit-trail",
                  path: "audit-trail",
                  element: <AuditTrailPage />,
                },
                {
                  id: "admin-persons",
                  path: "persons",
                  element: <AdminPersonPage />,
                },
              ],
            },
            {
              id: "catch-all",
              path: "*",
              element: <Navigate to="/" replace />,
            },
          ],
        },
      ],
    },
  ]);
};
